import React, { useRef, useState } from 'react';
import { Paperclip, Send, X } from 'lucide-react';
import { toast } from 'react-toastify';

const MAX_FILES = 5;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ALLOWED_TYPES = new Set([
  'image/jpeg', 'image/png', 'image/webp', 'application/pdf', 'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
]);

export function NoteComposer({ collaborationId, disabled = false, onSend }) {
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const fileInput = useRef(null);

  const addFiles = (event) => {
    const selected = Array.from(event.target.files || []);
    const valid = selected.filter((file) => ALLOWED_TYPES.has(file.type) && file.size <= MAX_FILE_BYTES);
    if (valid.length !== selected.length) toast.error('Use an allowed image/document of 10 MB or less.');
    setFiles((current) => {
      if (current.length + valid.length > MAX_FILES) toast.error(`A note can contain up to ${MAX_FILES} files.`);
      return [...current, ...valid].slice(0, MAX_FILES);
    });
    event.target.value = '';
  };

  const submit = (event) => {
    event.preventDefault();
    if (!text.trim() && files.length === 0) return;
    onSend({ text: text.trim(), files });
    setText('');
    setFiles([]);
  };

  return (
    <form className="collab-composer" onSubmit={submit}>
      <label className="sr-only" htmlFor={`collab-note-${collaborationId}`}>Shared note</label>
      <div className="collab-composer__field">
        <textarea
          id={`collab-note-${collaborationId}`}
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder="Write a note for the care team…"
          disabled={disabled}
          rows={2}
        />
        <div className="collab-composer__actions">
          <input ref={fileInput} hidden multiple type="file" accept="image/jpeg,image/png,image/webp,application/pdf,.doc,.docx,.xls,.xlsx,.txt" onChange={addFiles} />
          <button type="button" aria-label="Attach files" title="Attach files" onClick={() => fileInput.current?.click()} disabled={disabled || files.length >= MAX_FILES}>
            <Paperclip size={18} aria-hidden="true" />
          </button>
          <button type="submit" aria-label="Send note" title="Send note" disabled={disabled || (!text.trim() && files.length === 0)}>
            <Send size={18} aria-hidden="true" />
          </button>
        </div>
      </div>
      {files.length > 0 && (
        <div className="collab-files" aria-label="Files to upload">
          {files.map((file, index) => (
            <span key={`${file.name}-${file.lastModified}`}>
              {file.name}
              <button type="button" aria-label={`Remove ${file.name}`} onClick={() => setFiles((value) => value.filter((_, itemIndex) => itemIndex !== index))}>
                <X size={13} />
              </button>
            </span>
          ))}
        </div>
      )}
    </form>
  );
}

export default NoteComposer;
